import { useRef, useState } from "react";
import { Download, Share2, Images, Info } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const BATCH = 6;
const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
export const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
export const isAndroid = /Android/i.test(ua);

const extOf = (type) => (type?.includes("png") ? "png" : "jpg");
const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));

async function loadFiles(photos, code, offset) {
  return Promise.all(photos.map(async (p, i) => {
    const r = await api.get(`/media/${p.id}/clean`, { responseType: "blob" });
    const type = r.data.type || "image/jpeg";
    return new File([r.data], `${code}-${String(offset + i + 1).padStart(2, "0")}.${extOf(type)}`, { type });
  }));
}

function saveBlob(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const BatchRow = ({ photos, index, code, title }) => {
  const { t } = useLang();
  const files = useRef(null);
  const [state, setState] = useState("idle");
  const from = index * BATCH + 1;
  const prepare = async () => {
    setState("loading");
    try {
      files.current = await loadFiles(photos, code, index * BATCH);
      setState("ready");
    } catch {
      setState("idle");
      toast.error(t("saver.load_error"));
    }
  };
  const share = async () => {
    const list = files.current;
    if (navigator.canShare && navigator.canShare({ files: list })) {
      try { await navigator.share({ files: list, title }); } catch (e) { if (e.name !== "AbortError") toast.error(t("saver.share_error")); }
    } else {
      list.forEach((f, i) => setTimeout(() => saveBlob(f), i * 450));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 xd-panel px-4 py-3" data-testid={`saver-batch-${index}`}>
      <span className="text-sm text-zinc-300">{t("saver.batch")} <span className="ltr-num">{from}–{from + photos.length - 1}</span></span>
      {state !== "ready" ? (
        <button onClick={prepare} disabled={state === "loading"} className="btn-ghost !min-h-[2.75rem]" data-testid={`saver-prepare-${index}`}>
          <Images size={16} />{state === "loading" ? t("saver.preparing") : t("saver.prepare")}
        </button>
      ) : (
        <button onClick={share} className="btn-xd !min-h-[2.75rem]" data-testid={`saver-share-${index}`}>
          <Share2 size={16} />{isIOS ? t("saver.save_ios", { n: photos.length }) : t("saver.share")}
        </button>
      )}
    </div>
  );
};

export const PhotoSaver = ({ photos, code, title }) => {
  const { t } = useLang();
  const [dl, setDl] = useState(false);
  const downloadAll = async () => {
    setDl(true);
    try {
      for (let i = 0; i < photos.length; i += 1) {
        const [f] = await loadFiles([photos[i]], code, i);
        saveBlob(f);
        await new Promise((r) => setTimeout(r, 450));
      }
    } catch {
      toast.error(t("saver.load_error"));
    } finally {
      setDl(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="photo-saver">
      <div className="space-y-2">{chunk(photos, BATCH).map((b, i) => <BatchRow key={i} photos={b} index={i} code={code} title={title} />)}</div>
      {!isIOS && (
        <button onClick={downloadAll} disabled={dl} className="btn-ghost w-full" data-testid="saver-download-all">
          <Download size={16} />{dl ? t("saver.preparing") : t("saver.download_all")}
        </button>
      )}
      <div className="flex gap-3 text-xs text-zinc-400 leading-relaxed border border-white/10 p-4" data-testid="saver-help">
        <Info size={16} className="shrink-0 mt-0.5 text-zinc-500" />
        <div className="space-y-2">
          <p>{isIOS ? t("saver.help_ios") : isAndroid ? t("saver.help_android") : t("saver.help_desktop")}</p>
          <p className="text-zinc-500">{t("saver.help_quality")}</p>
        </div>
      </div>
    </div>
  );
};

export const singleDownloadUrl = (id) => `${API}/media/${id}/clean?download=true`;
